<script setup lang="ts">
const buttonStyle = {
  'primary': 'btn--primary',
  'secondary': 'btn--secondary',
  'dark-blue': 'btn--dark-blue',
  'icon': 'btn--icon',
  'plain': 'btn--plain',
  'plain-bold': 'btn--plain-bold',
  'footer-link': 'btn--footer-link',
} as const

const buttonSize = {
  'large-adaptive': 'btn--large-adaptive',
  'regular': 'btn--regular',
} as const

type ButtonType = keyof typeof buttonStyle
type ButtonSize = keyof typeof buttonSize

withDefaults(
  defineProps<{
    buttonType?: 'button' | 'submit'
    type?: ButtonType
    size?: ButtonSize
    title?: string
    disabled?: boolean
    loading?: boolean
  }>(),
  {
    buttonType: 'button',
    type: 'plain',
    size: 'regular',
    title: '',
    disabled: false,
    loading: false,
  },
)

const emit = defineEmits<{
  click: []
}>()
</script>

<template>
  <button
    class="btn"
    :disabled="disabled || loading"
    :class="[buttonStyle[type], buttonSize[size]]"
    @click="emit('click')"
  >
    <slot v-if="!loading" />
    <p v-else>
      Loading...
    </p>
  </button>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/buttons';
@use '@/assets/styles/colors' as *;
@use '@/assets/styles/helpers' as *;

.btn {
  @extend %app-btn-base;

  text-decoration: none;
}

.btn--regular {
  @extend %app-btn-size-regular;
}

.btn--large-adaptive {
  @extend %app-btn-size-large-adaptive;
}

.btn--icon {
  padding: var(--app-spacer-12) var(--app-spacer-14);
}

.btn--plain {
  @extend %link-color;
  @extend %font-16-24-regular;

  padding: 0;
}

.btn--plain-bold {
  @extend %link-color;
  @extend %font-16-24-bold;

  padding: 0;
}

.btn--footer-link {
  @extend %footer-link-style;
}

.btn--primary {
  @extend %app-btn-primary;
}

.btn--secondary {
  @extend %app-btn-secondary;
}

.btn--dark-blue {
  @extend %app-btn-dark-blue;
}
</style>
